import { Alert, Button, HStack, VStack } from "@chakra-ui/react";
import { Download, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CommonLayout from "../CommonLayout";
import AllPayoutsTable from "./components/AllPayoutsTable";
import BalanceCards from "./components/BalanceCards";
import BatchesTable from "./components/BatchesTable";
import LedgerTable from "./components/LedgerTable";
import UploadFileModal from "./components/UploadFileModal";
import WithdrawalModal from "./components/WithdrawalModal";
import TopUpModal from "./components/TopUpModal";
import { useFetchApprovedTopUps } from "./hooks/useFetchApprovedTopUps";
import { formatSettlement } from "./mappers/payouts.mapper";

import { usePermissions } from "@/hooks/usePermissions";
import { useBuildingManagementEnabled } from "./hooks/useBuildingManagementEnabled";
import { useTopUpsEnabled } from "./hooks/useTopUpsEnabled";
import { Tabs } from "@chakra-ui/react";
import WithdrawalsTable from "./components/WithdrawalsTable";
import DownloadReportModal from "./components/DownloadReportModal";

export default function PayoutsContainer(): JSX.Element {
  const [isWithdrawalModalOpen, setWithdrawalModalOpen] = useState(false);
  const [isTopUpModalOpen, setTopUpModalOpen] = useState(false);
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const [isOpenDownloadReportModal, setIsOpenDownloadReportModal] =
    useState<boolean>(false);

  const { isPayoutPreparer, isPayoutApprover } = usePermissions();
  const isBuildingManagementEnabled = useBuildingManagementEnabled();
  const isTopUpsEnabled = useTopUpsEnabled();
  const { topUps: approvedTopUps } = useFetchApprovedTopUps();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(value);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "movimientos";
  const handleTabChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", "movimientos");
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const buttons = (
    <HStack gap={3} flexWrap="wrap" justify={{ md: "flex-end" }} w="full">
      {isPayoutPreparer && (
        <>
          <Button
            colorPalette="palomma"
            size="sm"
            onClick={() => setWithdrawalModalOpen(true)}
          >
            + Nuevo pago a cuenta propia
          </Button>
          <Button
            variant="outline"
            colorPalette="palomma"
            size="sm"
            onClick={() => setUploadModalOpen(true)}
          >
            <Upload size={16} /> Cargar lote de pagos
          </Button>
        </>
      )}
      {isTopUpsEnabled && isPayoutApprover && (
        <Button
          variant="outline"
          colorPalette="palomma"
          size="sm"
          onClick={() => setTopUpModalOpen(true)}
        >
          Cargar fondos
        </Button>
      )}
      <Button size="sm" onClick={() => setIsOpenDownloadReportModal(true)}>
        <Download size={16} />
        Descargar reporte de pagos
      </Button>
    </HStack>
  );

  const children = (
    <VStack gap={4} align="stretch">
      <BalanceCards />
      {approvedTopUps.map((topUp) => (
        <Alert.Root key={topUp.id} status="info" colorPalette="blue">
          <Alert.Indicator />
          <Alert.Title fontSize="sm">
            Fondos en camino: {formatCurrency(topUp.amount - topUp.fee)} se
            acreditan {formatSettlement(topUp)}.
          </Alert.Title>
        </Alert.Root>
      ))}
    </VStack>
  );

  const tabContentProps = {
    display: "flex",
    flexDirection: "column" as const,
    flex: "1",
    minH: 0,
    css: { "&[hidden]": { display: "none" } },
  };
  const table = (
    <Tabs.Root
      value={activeTab}
      onValueChange={(e) => handleTabChange(e.value)}
      variant="line"
      colorPalette="palomma"
      display="flex"
      flexDirection="column"
      flex="1"
      minH={0}
    >
      <Tabs.List bg="transparent" mb={4} flexShrink={0}>
        <Tabs.Trigger
          value="movimientos"
          data-tour="payouts-tab-movimientos"
          fontWeight="medium"
          pb={3}
        >
          Movimientos
        </Tabs.Trigger>
        <Tabs.Trigger
          value="lotes"
          data-tour="payouts-tab-lotes"
          fontWeight="medium"
          pb={3}
        >
          Lotes de dispersión
        </Tabs.Trigger>
        <Tabs.Trigger
          value="detalle"
          data-tour="payouts-tab-detalle"
          fontWeight="medium"
          pb={3}
        >
          Detalle de dispersiones
        </Tabs.Trigger>
        <Tabs.Trigger
          value="retiros"
          data-tour="payouts-tab-retiros"
          fontWeight="medium"
          pb={3}
        >
          Retiros a cuenta propia
        </Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value="movimientos" {...tabContentProps}>
        <LedgerTable />
      </Tabs.Content>
      <Tabs.Content value="lotes" {...tabContentProps}>
        <BatchesTable />
      </Tabs.Content>
      <Tabs.Content value="detalle" {...tabContentProps}>
        <AllPayoutsTable />
      </Tabs.Content>
      <Tabs.Content value="retiros" {...tabContentProps}>
        <WithdrawalsTable />
      </Tabs.Content>
    </Tabs.Root>
  );

  return (
    <>
      <CommonLayout
        title={isBuildingManagementEnabled ? "Pagos" : "Pagos a propietarios"}
        subtitle={
          isBuildingManagementEnabled
            ? "Consulta tu balance y gestiona pagos"
            : "Consulta tu balance y gestiona pagos a tus propietarios"
        }
        buttons={buttons}
        children={children}
        table={table}
      />
      <WithdrawalModal
        isOpen={isWithdrawalModalOpen}
        onClose={() => setWithdrawalModalOpen(false)}
      />
      <TopUpModal
        isOpen={isTopUpModalOpen}
        onClose={() => setTopUpModalOpen(false)}
      />
      <UploadFileModal
        isOpen={isUploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
      />
      <DownloadReportModal
        open={isOpenDownloadReportModal}
        onClose={() => setIsOpenDownloadReportModal(false)}
      />
    </>
  );
}
